import { useState, useEffect } from 'react';
import {
  getAuth,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updatePassword,
  onAuthStateChanged,
  User,
} from 'firebase/auth';
import useNotification from '../../notification/useNotification';
import { useDispatch } from 'react-redux';
import { authFirebase } from '../../../config/firebase';
import { actions as spinnerActions } from '../../../components/ui/spinner/spinnerSlice';

const useChangePassword = (onHide: () => void) => {
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [passwordError, setPasswordError] = useState<string>('');
  const [confirmPasswordError, setConfirmPasswordError] = useState<string>('');
  const [user, setUser] = useState<User>();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(authFirebase, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const { showSuccessNotification, showWarningNotification } =
    useNotification();
  const dispatch = useDispatch();

  const validatePassword = (value: string): void => {
    const passwordRegex =
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    if (!passwordRegex.test(value)) {
      setPasswordError(
        'Password must be at least 8 characters, include a number, and a special character.'
      );
    } else {
      setPasswordError('');
    }
  };

  const handleNewPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = e.target.value;
    setNewPassword(value);
    validatePassword(value);
  };

  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = e.target.value;
    setConfirmPassword(value);
    if (value !== newPassword) {
      setConfirmPasswordError('Passwords do not match.');
    } else {
      setConfirmPasswordError('');
      setLoading(false);
    }
  };

  const getErrorMessage = (err: unknown): string => {
    let message = 'An error occurred while changing the password.';

    if (typeof err === 'object' && err !== null) {
      const errorObj = err as { code?: string; message?: string };

      switch (errorObj.code) {
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          return 'Current password is incorrect.';
        case 'auth/weak-password':
          return 'New password is too weak. It should be at least 6 characters.';
        case 'auth/requires-recent-login':
          return 'This operation is sensitive and requires recent authentication. Please log in again.';
        case 'auth/user-mismatch':
          return 'The credentials do not correspond to the currently logged in user.';
        case 'auth/user-not-found':
          return 'User not found.';
        case 'auth/too-many-requests':
          return 'Too many failed attempts. Please try again later.';
        case 'auth/network-request-failed':
          return 'Network error. Please check your connection and try again.';
        default:
          if (errorObj.message === 'INVALID_LOGIN_CREDENTIALS') {
            return 'Invalid current password.';
          } else if (errorObj.message) {
            return errorObj.message;
          }
      }
    }

    return message;
  };

  const handlePasswordChange = async () => {
    if (!user?.email) {
      showWarningNotification('User not authenticated.');
      return;
    }

    if(currentPassword == newPassword) {
      showWarningNotification('New password must be different from your current password.');
      return;
    }

    dispatch(spinnerActions.showSpinner());
    const credential = EmailAuthProvider.credential(
      user.email,
      currentPassword
    );
    onHide();
    try {
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      showSuccessNotification('Password changed successfully.');
    } catch (err) {
      const message: string = getErrorMessage(err);
      showWarningNotification(message);
    } finally {
      dispatch(spinnerActions.hideSpinner());
    }
  };

  return {
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    passwordError,
    setPasswordError,
    confirmPasswordError,
    setConfirmPasswordError,
    handleNewPasswordChange,
    handleConfirmPasswordChange,
    handlePasswordChange,
    loading,
  };
};

export default useChangePassword;
